import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex]:not([tabindex="-1"])');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage = imagesToTransition?.[0];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLElement} [mediaContainer] - The media container element.
 * @property {HTMLElement} [badgeContainer] - The badge container element.
 * @property {HTMLElement} [imageBadgeContainer] - The image badge container element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  /** @type {Map<string, {url: string, alt: string, fp: string}>} */
  #mediaMap = new Map();

  /** @type {string | null} */
  #currentMediaId = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #resetTimer = null;

  /** @type {number} */
  #swapGeneration = 0;

  /** @type {{badgeHtml: string, imageBadgeHtml: string | null, hasBadges: boolean, hasImageBadges: boolean} | null} */
  #savedBadgeState = null;

  /** @type {number} */
  #badgeSwapGeneration = 0;

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    this.addEventListener('click', this.navigateToProduct);

    this.#parseMediaMap();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    clearTimeout(this.#resetTimer);
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImage(event);
    this.#updateBadges(event);

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Updates the primary image when a variant is selected via the server-rendered HTML.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateVariantImage(event) {
    const { mediaContainer } = this.refs;
    if (!mediaContainer) return;

    const newCard = event.detail.data.html?.querySelector('[ref="mediaContainer"]');
    if (newCard) {
      morph(mediaContainer, newCard);
      this.#currentMediaId = mediaContainer.dataset.mediaId || this.#currentMediaId;
    }
  }

  /**
   * Updates the badge container when a variant is selected via the server-rendered HTML.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateBadges(event) {
    const { badgeContainer, imageBadgeContainer, cardGallery } = this.refs;
    const newHtml = event.detail.data.html;

    if (badgeContainer) {
      const newBadges = newHtml?.querySelector('[ref="badgeContainer"]');
      if (newBadges) {
        morph(badgeContainer, newBadges);
      }
    }

    // Update image badges (top-right)
    const newImageBadges = newHtml?.querySelector('[ref="imageBadgeContainer"]');
    if (imageBadgeContainer && newImageBadges) {
      morph(imageBadgeContainer, newImageBadges);
    } else if (imageBadgeContainer && !newImageBadges) {
      imageBadgeContainer.remove();
    } else if (!imageBadgeContainer && newImageBadges && cardGallery) {
      cardGallery.appendChild(newImageBadges.cloneNode(true));
    }

    // Update badge modifier classes on card gallery
    if (cardGallery) {
      const newGallery = newHtml?.querySelector('[ref="cardGallery"]');
      if (newGallery) {
        cardGallery.classList.toggle('card-gallery--badge-top-left', newGallery.classList.contains('card-gallery--badge-top-left'));
        cardGallery.classList.toggle('card-gallery--badge-top-right', newGallery.classList.contains('card-gallery--badge-top-right'));
      }
    }
  }

  /**
   * Sets the product card image to a specific variant's media.
   * Called by quick-add-component when a variant is selected (swatch or dropdown).
   * @param {string} mediaId - The media ID to display.
   */
  setVariantMedia(mediaId) {
    clearTimeout(this.#resetTimer);
    this.#currentMediaId = mediaId;
    this.#savedBadgeState = null;
    this.#swapPrimaryImage(mediaId);
  }

  /**
   * Previews a variant image by swapping the primary image.
   * Called on swatch pointerenter.
   * @param {string} mediaId - The media id of the variant to preview.
   */
  previewVariant(mediaId) {
    clearTimeout(this.#resetTimer);
    if (!mediaId) return;
    this.#swapPrimaryImage(String(mediaId));
  }

  /**
   * Builds badge HTML array from variant data.
   * @param {{available: boolean, badges: Array<{title: string, bg: string, text: string}>, soldOutColor: string, soldOutText: string}} data
   * @returns {string[]}
   */
  #buildBadgeHtml(data) {
    const html = [];

    if (!data.available) {
      html.push(
        `<div class="product-badges__badge product-badges__badge--rectangle ${data.soldOutColor}">${data.soldOutText}</div>`
      );
    }

    if (data.badges) {
      for (const badge of data.badges) {
        html.push(
          `<div class="product-badges__badge product-badges__badge--rectangle product-badges__badge--custom" style="--badge-custom-bg: ${badge.bg}; --badge-custom-text: ${badge.text};">${badge.title}</div>`
        );
      }
    }

    return html;
  }

  /**
   * Builds image badge HTML from variant data.
   * @param {Array<{src: string, srcset: string, alt: string}>} imageBadges
   * @returns {string}
   */
  #buildImageBadgeHtml(imageBadges) {
    return imageBadges
      .map((b) => `<img src="${b.src}" srcset="${b.srcset}" sizes="85px" alt="${b.alt}" loading="lazy" class="product-badges__image-badge" width="85" height="auto">`)
      .join('');
  }

  /**
   * Crossfades a badge container from its current content to new HTML.
   * Fades out with blur, swaps content, fades back in.
   * @param {HTMLElement} container - The badge container element.
   * @param {string} newHtml - The new innerHTML to display.
   * @param {number} generation - Generation counter to prevent stale transitions.
   */
  #crossfadeBadgeContainer(container, newHtml, generation) {
    // If content is identical, skip transition
    if (container.innerHTML === newHtml) return;

    // Fade out with blur
    container.classList.add('product-badges--fading');

    const swapAndFadeIn = () => {
      if (this.#badgeSwapGeneration !== generation) return;
      container.innerHTML = newHtml;
      container.classList.remove('product-badges--fading');
    };

    container.addEventListener('transitionend', swapAndFadeIn, { once: true });
    setTimeout(swapAndFadeIn, 300);
  }

  /**
   * Previews badges for a hovered variant with crossfade + blur transition.
   * Saves current badge state for restoration on pointerleave.
   * @param {{available: boolean, badges: Array<{title: string, bg: string, text: string}>, imageBadges: Array<{src: string, srcset: string, alt: string}>, soldOutColor: string, soldOutText: string}} variantBadgeData
   */
  previewBadges(variantBadgeData) {
    const { cardGallery } = this.refs;
    const badgeContainer = this.querySelector('[ref="badgeContainer"]');
    if (!badgeContainer) return;

    const generation = ++this.#badgeSwapGeneration;

    // Save current badge state on first preview
    if (!this.#savedBadgeState) {
      const imageBadgeContainer = this.querySelector('[ref="imageBadgeContainer"]');
      this.#savedBadgeState = {
        badgeHtml: badgeContainer.innerHTML,
        imageBadgeHtml: imageBadgeContainer ? imageBadgeContainer.innerHTML : null,
        hasBadges: cardGallery?.classList.contains('card-gallery--badge-top-left') || false,
        hasImageBadges: cardGallery?.classList.contains('card-gallery--badge-top-right') || false,
      };
    }

    // Text badges
    const badgeHtml = this.#buildBadgeHtml(variantBadgeData);
    this.#crossfadeBadgeContainer(badgeContainer, badgeHtml.join(''), generation);

    // Image badges
    const hasImageBadges = variantBadgeData.imageBadges && variantBadgeData.imageBadges.length > 0;
    let imageBadgeContainer = this.querySelector('[ref="imageBadgeContainer"]');

    if (hasImageBadges) {
      const imgHtml = this.#buildImageBadgeHtml(variantBadgeData.imageBadges);

      if (imageBadgeContainer) {
        this.#crossfadeBadgeContainer(imageBadgeContainer, imgHtml, generation);
      } else if (cardGallery) {
        const div = document.createElement('div');
        div.className = 'product-badges product-badges--top-right product-badges--fading';
        div.setAttribute('ref', 'imageBadgeContainer');
        div.innerHTML = imgHtml;
        cardGallery.appendChild(div);
        void div.offsetHeight;
        div.classList.remove('product-badges--fading');
      }
    } else if (imageBadgeContainer) {
      imageBadgeContainer.classList.add('product-badges--fading');
      const remove = () => {
        if (this.#badgeSwapGeneration !== generation) return;
        imageBadgeContainer.remove();
      };
      imageBadgeContainer.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, 300);
    }

    // Update modifier classes
    if (cardGallery) {
      cardGallery.classList.toggle('card-gallery--badge-top-left', badgeHtml.length > 0);
      cardGallery.classList.toggle('card-gallery--badge-top-right', hasImageBadges);
    }
  }

  /**
   * Restores badges to the saved state with crossfade + blur transition.
   */
  restoreBadges() {
    if (!this.#savedBadgeState) return;

    const { cardGallery } = this.refs;
    const badgeContainer = this.querySelector('[ref="badgeContainer"]');
    if (!badgeContainer) return;

    const generation = ++this.#badgeSwapGeneration;

    // Crossfade text badges back
    this.#crossfadeBadgeContainer(badgeContainer, this.#savedBadgeState.badgeHtml, generation);

    // Crossfade image badges back
    const imageBadgeContainer = this.querySelector('[ref="imageBadgeContainer"]');

    if (this.#savedBadgeState.imageBadgeHtml != null) {
      if (imageBadgeContainer) {
        this.#crossfadeBadgeContainer(imageBadgeContainer, this.#savedBadgeState.imageBadgeHtml, generation);
      } else if (cardGallery) {
        const div = document.createElement('div');
        div.className = 'product-badges product-badges--top-right product-badges--fading';
        div.setAttribute('ref', 'imageBadgeContainer');
        div.innerHTML = this.#savedBadgeState.imageBadgeHtml;
        cardGallery.appendChild(div);
        void div.offsetHeight;
        div.classList.remove('product-badges--fading');
      }
    } else if (imageBadgeContainer) {
      imageBadgeContainer.classList.add('product-badges--fading');
      const remove = () => {
        if (this.#badgeSwapGeneration !== generation) return;
        imageBadgeContainer.remove();
      };
      imageBadgeContainer.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, 300);
    }

    // Restore modifier classes
    if (cardGallery) {
      cardGallery.classList.toggle('card-gallery--badge-top-left', this.#savedBadgeState.hasBadges);
      cardGallery.classList.toggle('card-gallery--badge-top-right', this.#savedBadgeState.hasImageBadges);
    }

    this.#savedBadgeState = null;
  }

  /**
   * Parses the media map JSON data from the card gallery.
   */
  #parseMediaMap() {
    const script = this.querySelector('script[data-media-map]');
    if (!script?.textContent) return;

    try {
      const data = JSON.parse(script.textContent);
      for (const [id, info] of Object.entries(data)) {
        this.#mediaMap.set(id, info);
      }
    } catch {
      // No media data available
    }

    const { mediaContainer } = this.refs;
    if (mediaContainer) {
      this.#currentMediaId = mediaContainer.dataset.mediaId || null;
    }
  }

  /**
   * Swaps the primary card image with a crossfade transition.
   * Uses an overlay image that fades in, then updates the primary underneath.
   * @param {string} mediaId - The media ID to display.
   */
  #swapPrimaryImage(mediaId) {
    const mediaData = this.#mediaMap.get(String(mediaId));
    if (!mediaData) return;

    const { mediaContainer } = this.refs;
    if (!mediaContainer) return;

    const primaryImage = mediaContainer.querySelector('.card-gallery__primary-image');
    if (!(primaryImage instanceof HTMLImageElement)) return;

    if (mediaContainer.dataset.mediaId === mediaId) return;
    mediaContainer.dataset.mediaId = mediaId;

    const widths = [240, 352, 832, 1200, 1600, 1920];
    const srcset = widths.map((w) => `${mediaData.url.replace(/(width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
    const src = mediaData.url.replace(/(width=)\d+/, '$11920');

    const generation = ++this.#swapGeneration;

    // Get or create persistent crossfade overlay
    let overlay = mediaContainer.querySelector('.card-gallery__crossfade-image');
    if (!(overlay instanceof HTMLImageElement)) {
      overlay = document.createElement('img');
      overlay.className = 'card-gallery__crossfade-image';
      overlay.setAttribute('aria-hidden', 'true');
      mediaContainer.appendChild(overlay);
    }

    // Reset overlay to invisible without transition
    overlay.style.transition = 'none';
    overlay.style.opacity = '0';

    // Set new image on the overlay
    overlay.srcset = srcset;
    overlay.src = src;
    overlay.alt = mediaData.alt || '';
    overlay.style.objectPosition = mediaData.fp || '';

    // Force browser to commit the opacity:0 state before transitioning
    void overlay.offsetHeight;

    // Re-enable CSS transition and fade in
    overlay.style.transition = '';
    overlay.style.opacity = '1';

    // After transition, update primary underneath and hide overlay
    const finalize = () => {
      if (this.#swapGeneration !== generation) return;

      primaryImage.srcset = srcset;
      primaryImage.src = src;
      primaryImage.alt = mediaData.alt;
      primaryImage.style.objectPosition = mediaData.fp || '';
      overlay.style.opacity = '0';
    };

    overlay.addEventListener('transitionend', finalize, { once: true });
    setTimeout(finalize, 600);
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = async (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex]:not([tabindex="-1"])');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    // If the click was anywhere inside the quick add block, do not navigate.
    if (event.target.closest('quick-add-component')) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      await yieldToMainThread();
      history.replaceState({}, '', url.toString());
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the primary image to the currently selected variant.
   * Debounced via setTimeout to avoid flicker when moving between swatches.
   */
  resetVariant = () => {
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => {
      if (this.#currentMediaId) {
        this.#swapPrimaryImage(this.#currentMediaId);
      }
      this.restoreBadges();
    }, 100);
  };
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
